// Ship Documents dialog (docs-ux): the Type / Folder pick lists and the extra files of a document.
// Pure functions (no React, no network) - tests/docs-ux.test.mjs.
import { uploadProblem, UPLOAD_MAX_MB } from './uploadTypes.js'
import { storedPath } from './filePaths.js'

export const STANDARD_TYPES = ['Certificate', 'Manual', 'Survey report', 'Licence', 'Registration', 'Insurance', 'Class', 'Flag', 'Other']
export const NEW_TYPE = '+ New type...'
export const NEW_FOLDER = '+ New folder...'
export const MAX_ATTACHMENTS = 30   // the database refuses more (sql/docs-ux.sql hc_doc_attachments_ok)

const val = v => (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) ? (v.value ?? '') : (v ?? '')
const clean = s => String(s == null ? '' : s).replace(/\s+/g, ' ').trim()

// the vessel's own values of one field: distinct (case and spaces ignored, the most used spelling wins), most used first,
// then A-Z; then the standard ones the vessel has not used yet, in their own order
export function fieldOptions(rows, keys, standard = []) {
  const by = new Map()   // lower -> { label, n, spellings }
  for (const r of rows || []) {
    let v = ''
    for (const k of [].concat(keys)) { v = clean(val(r && r[k])); if (v) break }
    if (!v || v === '-') continue
    const low = v.toLowerCase()
    const e = by.get(low) || { n: 0, sp: new Map() }
    e.n++; e.sp.set(v, (e.sp.get(v) || 0) + 1)
    by.set(low, e)
  }
  const own = [...by.values()].map(e => ({ label: [...e.sp.entries()].sort((a, b) => b[1] - a[1] || (/^[A-Z]/.test(b[0]) - /^[A-Z]/.test(a[0])) || a[0].localeCompare(b[0]))[0][0], n: e.n }))
    .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  const seen = new Set(own.map(o => o.label.toLowerCase()))
  const std = standard.filter(s => !seen.has(s.toLowerCase())).map(s => ({ label: s, n: 0, standard: true }))
  return [...own, ...std]
}

export const typeOptions = rows => fieldOptions(rows, ['Document Type', 'document_type'], STANDARD_TYPES)
export const folderOptions = rows => fieldOptions(rows, ['Folder', 'folder'])

// what the combobox shows for a typed text: the options containing it (starting with it first)
export function filterOptions(options, text) {
  const q = clean(text).toLowerCase()
  if (!q) return options
  const hit = options.filter(o => o.label.toLowerCase().includes(q))
  return [...hit.filter(o => o.label.toLowerCase().startsWith(q)), ...hit.filter(o => !o.label.toLowerCase().startsWith(q))]
}

// a typed value that matches an existing option (any case) is saved in that option's spelling: no "certificate" next to "Certificate"
export function canonical(options, text) {
  const v = clean(text)
  const o = (options || []).find(x => x.label.toLowerCase() === v.toLowerCase())
  return o ? o.label : v
}

// ---- extra files ----

// stored value -> a clean list of { path, name, size, type } (anything that is no file of the documents area is dropped)
export function normAttachments(v) {
  let list = v
  if (typeof list === 'string') { try { list = JSON.parse(list) } catch (e) { list = [] } }
  if (!Array.isArray(list)) return []
  return list.map(a => (typeof a === 'string' ? { path: a } : a)).filter(a => a && typeof a.path === 'string' && /^documents\//.test(storedPath(a.path) || ''))
    .map(a => ({ path: a.path, name: clean(a.name).slice(0, 200) || a.path.replace(/^.*\/\d+_/, ''), size: Number.isFinite(Number(a.size)) && a.size !== '' && a.size != null ? Number(a.size) : null, type: clean(a.type).slice(0, 120) }))
}

export function sameAttachments(a, b) {
  const x = normAttachments(a), y = normAttachments(b)
  return x.length === y.length && x.every((f, i) => f.path === y[i].path && f.name === y[i].name)
}

export function fmtSize(n) {
  if (n == null || n === '') return ''
  const b = Number(n)
  if (!Number.isFinite(b) || b < 0) return ''
  if (b < 1024) return b + ' B'
  if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB'
  return (b / 1024 / 1024).toFixed(b < 10 * 1024 * 1024 ? 1 : 0) + ' MB'
}

// the extra files a picker / drop may add: PDF, photos, Office, text (the bucket's list, without videos)
export const ATTACH_ACCEPT = 'application/pdf,image/*,.doc,.docx,.xls,.xlsx,.txt,.csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv'
const OFFICE_EXT = /\.(pdf|jpe?g|png|webp|gif|heic|heif|docx?|xlsx?|txt|csv)$/i

// files dropped / chosen -> { ok: [File], bad: ['name: why'] }; the same rule as every upload (src/lib/uploadTypes.js) plus
// "no videos here" and the 30-file limit
export function splitFiles(files, already = 0) {
  const ok = [], bad = []
  for (const f of Array.from(files || [])) {
    const name = String((f && f.name) || 'file')
    const p = uploadProblem(f)
    if (p) { bad.push(name + ': ' + p); continue }
    const type = String(f.type || '').toLowerCase()
    if (/^video\//.test(type) || (!OFFICE_EXT.test(name) && !/^(application\/pdf|image\/)/.test(type))) { bad.push(name + ': use a PDF, a photo or an Office file'); continue }
    if (already + ok.length >= MAX_ATTACHMENTS) { bad.push(name + ': at most ' + MAX_ATTACHMENTS + ' files per document'); continue }
    ok.push(f)
  }
  return { ok, bad }
}
export const ATTACH_HINT = 'PDF, photos, Word, Excel - up to ' + UPLOAD_MAX_MB + ' MB each'

// the record's new value after adding uploaded files / removing one
export const addAttachments = (list, added) => [...normAttachments(list), ...normAttachments(added)].slice(0, MAX_ATTACHMENTS)
export const removeAttachment = (list, path) => normAttachments(list).filter(a => a.path !== path)
